import { makeEvent, SOURCE } from '../model';
import toEpochSeconds from '../timestamps';

// Door state and the server flags are snapshots, not events, so we derive events
// from TRANSITIONS across the series. Consequence: a derived beat lands at the
// heartbeat sample time, not the moment the door actually moved.

function doorStatusOf(hb) {
  return (
    hb?.server?.jofemarStateV2?.doorStatus ??
    hb?.jofemarStateV2?.doorStatus ??
    null
  );
}
function safetyHoldOf(hb) {
  return Boolean(hb?.server?.safetyHold?.enabled);
}
function outOfOrderOf(hb) {
  return Boolean(hb?.server?.outOfOrder?.enabled);
}

export function fromHeartbeat(series = []) {
  // Normalize once up front so the ordering and the emitted events agree, and so
  // this adapter reads timestamps the same way every other one does.
  const sorted = series
    .map((hb) => ({ hb, ts: toEpochSeconds(hb.timestamp) }))
    .sort((a, b) => a.ts - b.ts);
  const events = [];
  let prevDoor = null;
  let prevHold = null;
  let prevOOO = null;

  for (const { hb, ts } of sorted) {
    const door = doorStatusOf(hb);
    if (door && prevDoor && door !== prevDoor) {
      // Use the observed prevDoor: hardcoding the "from" side can describe a
      // transition that never happened.
      const summary = `Door transitioned ${prevDoor} → ${door}`;
      if (door === 'OPENED') {
        events.push(
          makeEvent({
            ts,
            source: SOURCE.HEARTBEAT,
            type: 'DOOR_OPEN',
            summary,
            raw: hb,
          }),
        );
      } else if (door === 'CLOSED') {
        events.push(
          makeEvent({
            ts,
            source: SOURCE.HEARTBEAT,
            type: 'DOOR_CLOSE',
            summary,
            raw: hb,
          }),
        );
      }
    }
    if (door) prevDoor = door;

    const hold = safetyHoldOf(hb);
    if (prevHold === false && hold === true) {
      events.push(
        makeEvent({
          ts,
          source: SOURCE.HEARTBEAT,
          type: 'SAFETY_HOLD',
          summary: 'Safety hold engaged (temperature out of safe range)',
          raw: hb.server?.safetyHold,
        }),
      );
    }
    prevHold = hold;

    const ooo = outOfOrderOf(hb);
    if (prevOOO === false && ooo === true) {
      events.push(
        makeEvent({
          ts,
          source: SOURCE.HEARTBEAT,
          type: 'OUT_OF_ORDER',
          summary: 'Fridge marked out of order',
          raw: hb.server?.outOfOrder,
        }),
      );
    }
    prevOOO = ooo;
  }
  return events;
}
